import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { formatPhoneNumber } from "@/lib/phoneUtils";

interface Contact {
  _id: Id<"onboardingContacts">;
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  type: "client" | "location" | "additional";
}

interface EditOnboardingContactSheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contact: Contact;
  onContactUpdated?: () => void;
}

export default function EditOnboardingContactSheet({
  open,
  onOpenChange,
  contact,
  onContactUpdated,
}: EditOnboardingContactSheetProps) {
  const [name, setName] = useState(contact.name);
  const [email, setEmail] = useState(contact.email || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [title, setTitle] = useState(contact.title || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const updateContact = useMutation(api.onboardingContacts.updateContact);

  // Reset form when contact changes
  useEffect(() => {
    setName(contact.name);
    setEmail(contact.email || "");
    setPhone(contact.phone || "");
    setTitle(contact.title || "");
  }, [contact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Contact name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await updateContact({
        id: contact._id,
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        title: title.trim() || undefined,
      });

      toast({
        title: "Success!",
        description: "Contact updated successfully!",
      });

      if (onContactUpdated) onContactUpdated();
      if (onOpenChange) onOpenChange(false);
    } catch (error) {
      console.error("Error updating contact:", error);
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getContactTypeLabel = (type: string) => {
    switch (type) {
      case "client":
        return "Client Contact";
      case "location":
        return "Location Contact";
      case "additional":
        return "Additional Contact";
      default:
        return "Contact";
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Edit Contact</SheetTitle>
          <SheetDescription>
            Update the contact information for this{" "}
            {getContactTypeLabel(contact.type).toLowerCase()}.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {contact.type !== "additional" && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-800">
                This is a {getContactTypeLabel(contact.type).toLowerCase()} and cannot be removed
                from the onboarding.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter contact name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Facility Manager, Operations Director"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              placeholder="(555) 555-5555"
              maxLength={14}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Contact"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
